'use client';
import React from 'react';

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#050a0f] grid-bg-animated">
      {/* Animated background layer */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Glowing orbs */}
        <div className="orb orb-cyan" style={{ width: '600px', height: '600px', top: '-100px', left: '-150px' }} />
        <div className="orb orb-blue" style={{ width: '500px', height: '500px', top: '20%', right: '-100px' }} />
        <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '0', left: '30%' }} />

        {/* Scan line */}
        <div className="scan-line" />

        {/* Floating particles */}
        <div className="particle particle-1" style={{ top: '20%', left: '10%' }} />
        <div className="particle particle-2" style={{ top: '40%', left: '25%' }} />
        <div className="particle particle-3" style={{ top: '60%', left: '50%' }} />
        <div className="particle particle-4" style={{ top: '30%', right: '20%' }} />
        <div className="particle particle-5" style={{ top: '70%', right: '35%' }} />
        <div className="particle particle-6" style={{ top: '15%', right: '10%' }} />
        <div className="particle particle-7" style={{ top: '80%', left: '15%' }} />
        <div className="particle particle-8" style={{ top: '50%', right: '5%' }} />

        {/* Animated pixel mosaic squares */}
        <div className="absolute top-16 left-[5%] w-16 h-16 bg-[#181b1f] pixel-float-1" style={{ opacity: 0.6 }} />
        <div className="absolute top-32 left-[8%] w-10 h-10 bg-[#222] pixel-float-2" style={{ opacity: 0.4 }} />
        <div className="absolute top-20 left-[14%] w-12 h-12 bg-[#181b1f] pixel-float-3" style={{ opacity: 0.5 }} />
        <div className="absolute top-48 left-[3%] w-8 h-8 bg-[#222] pixel-float-4" style={{ opacity: 0.3 }} />
        <div className="absolute top-16 right-[5%] w-16 h-16 bg-[#181b1f] pixel-float-2" style={{ opacity: 0.6 }} />
        <div className="absolute top-32 right-[8%] w-10 h-10 bg-[#222] pixel-float-3" style={{ opacity: 0.4 }} />
        <div className="absolute top-20 right-[14%] w-12 h-12 bg-[#181b1f] pixel-float-1" style={{ opacity: 0.5 }} />
        <div className="absolute top-48 right-[3%] w-8 h-8 bg-[#222] pixel-float-5" style={{ opacity: 0.3 }} />
        <div className="absolute top-[40%] left-[2%] w-20 h-20 bg-[#181b1f] pixel-float-4" style={{ opacity: 0.4 }} />
        <div className="absolute top-[50%] right-[2%] w-20 h-20 bg-[#181b1f] pixel-float-6" style={{ opacity: 0.4 }} />
        <div className="absolute top-[60%] left-[6%] w-12 h-12 bg-[#222] pixel-float-2" style={{ opacity: 0.3 }} />
        <div className="absolute top-[55%] right-[6%] w-12 h-12 bg-[#222] pixel-float-1" style={{ opacity: 0.3 }} />
        <div className="absolute bottom-32 left-[4%] w-16 h-16 bg-[#181b1f] pixel-float-5" style={{ opacity: 0.5 }} />
        <div className="absolute bottom-20 left-[10%] w-10 h-10 bg-[#222] pixel-float-3" style={{ opacity: 0.4 }} />
        <div className="absolute bottom-32 right-[4%] w-16 h-16 bg-[#181b1f] pixel-float-6" style={{ opacity: 0.5 }} />
        <div className="absolute bottom-20 right-[10%] w-10 h-10 bg-[#222] pixel-float-4" style={{ opacity: 0.4 }} />

        {/* Vertical grid lines with animation */}
        <div className="absolute top-0 bottom-0 left-[8.33%] border-l border-[#ffffff08] grid-line-animated" />
        <div className="absolute top-0 bottom-0 left-[33.33%] border-l border-[#ffffff08] grid-line-animated" style={{ animationDelay: '1s' }} />
        <div className="absolute top-0 bottom-0 left-[66.66%] border-l border-[#ffffff08] grid-line-animated" style={{ animationDelay: '2s' }} />
        <div className="absolute top-0 bottom-0 right-[8.33%] border-l border-[#ffffff08] grid-line-animated" style={{ animationDelay: '0.5s' }} />

        {/* Horizontal sweep lines */}
        <div className="line-sweep" style={{ top: '30%', animationDelay: '0s' }} />
        <div className="line-sweep" style={{ top: '60%', animationDelay: '2.5s' }} />
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12 pt-24 pb-16 w-full">
        <div className="flex flex-col items-start gap-12">
          {/* Hero Text */}
          <div className="flex-1 max-w-5xl">
            {/* Hidden h1 for SEO */}
            <h1 className="sr-only">TechSonar, DevSecOps, AppSec & Platform Security Consultancy</h1>

            {/* Eyebrow label */}
            <div className="hero-text-1 flex items-center gap-3 mb-6">
              <div className="w-6 h-px bg-[#80edfa]" />
              <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">
                DevSecOps &amp; Platform Security
              </span>
            </div>

            {/* Large display text */}
            <div className="font-heading font-medium leading-none tracking-tight">
              <div className="hero-text-2 text-[clamp(3rem,8.5vw,7.5rem)] text-[#f5f5f8] uppercase">
                SECURITY
              </div>

              <div className="hero-text-3 flex items-center gap-6 my-1">
                <div className="text-[clamp(3rem,8.5vw,7.5rem)] text-[#f5f5f8] uppercase gradient-text-animate">
                  ENGINEERED
                </div>
                {/* Side annotation */}
                <div className="hidden lg:flex flex-col gap-1 ml-2">
                  <span className="font-mono-brand text-[0.6rem] text-[#555] tracking-widest uppercase leading-tight">
                    NOT AUDITED
                  </span>
                  <span className="font-mono-brand text-[0.6rem] bg-[#80edfa] text-[#050a0f] px-2 py-0.5 inline-block tracking-widest uppercase">
                    CONTINUOUSLY
                  </span>
                </div>
              </div>

              <div className="hero-text-1 text-[clamp(3rem,8.5vw,7.5rem)] text-[#f5f5f8] uppercase">
                IN.
              </div>
            </div>

            {/* Sub-headline */}
            <p className="mt-8 text-[#aaa] text-base leading-relaxed max-w-2xl">
              TechSonar embeds senior security engineers directly into your organisation. We deliver AppSec (SAST, DAST, SCA), CVE triage and enrichment, threat modelling, risk-led security programmes, and continuous compliance across SOC2, ISO 27001, DORA, and GDPR, not as a one-off engagement, but as an ongoing, embedded capability that keeps your security posture strong every day of the year.
            </p>

            {/* Differentiator pills */}
            <div className="hero-text-2 flex flex-wrap gap-3 mt-6">
              {['Embedded, not external', 'Continuous, not point-in-time', 'Risk-led, not checkbox-driven', 'Senior engineers only']?.map((pill, i) => (
                <span
                  key={i}
                  className="font-mono-brand text-[0.6rem] uppercase tracking-widest border border-[#333] text-[#666] px-3 py-1.5"
                >
                  {pill}
                </span>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="hero-cta flex flex-wrap items-center gap-4 mt-10">
              <a href="#contact" className="btn-primary">
                Book a consultation
              </a>
              <a href="#capabilities" className="btn-secondary">
                Explore our capabilities
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
          <span className="font-mono-brand text-[0.6rem] text-[#666] uppercase tracking-widest">Scroll</span>
          <div className="scroll-indicator w-px h-8 bg-gradient-to-b from-[#80edfa] to-transparent" />
        </div>
      </div>
    </section>
  );
}
